import { Prose } from '../../components/Prose'
import { useUsage } from '../../api/hooks'
import type { UsageRow } from '../../api/client'
import { fmt, todayIso, trDate } from '../../lib/format'
import {
  AKTIFLIK_ETIKET, GOAL_ETIKET, aktiflik, grupla, gunceGore, ilerlemeYuzde, kalanKg,
  ozet, siralaKullanicilar,
} from '../../lib/usage'

/**
 * Kullanım özeti — "kim ne kadar kullanıyor".
 *
 * Google Analytics'in cevapladığı soru bu değil: o sayfa görüntülemesi sayar,
 * kullanıcı değil. Burada sorulan "arkadaşım paneli açtı mı" değil, "antrenman
 * yapıyor mu" — ve bu sayı zaten veritabanında duruyor, dışarıya bir şey
 * göndermeye gerek yok.
 *
 * "Aktif gün" bilerek KAYIT bırakılan gün: uygulamayı açıp kapatmak aktiflik
 * değil. Üç kişilik bir panelde ziyaret saymanın kimseye faydası yok.
 */
export function UsagePanel() {
  const q = useUsage()
  const bugun = todayIso()

  if (q.isLoading) {
    return <div className="card"><p className="sub">Kullanım verisi yükleniyor…</p></div>
  }
  if (q.error || !q.data) {
    return (
      <div className="card">
        <h2>Kullanım</h2>
        <p className="sub">Veri alınamadı. Sunucu güncel mi?</p>
      </div>
    )
  }

  const rows = siralaKullanicilar(q.data.users)
  const o = ozet(rows)
  const gruplar = grupla(q.data.feed)

  return (
    <>
      <div className="card">
        <h2>Kullanım</h2>
        <Prose className="card-h">
          Sayılar bu sunucudaki verinin kendisinden geliyor; hiçbir yere bir şey gönderilmiyor.
          <strong> Aktif gün</strong> = son 30 günde kayıt bırakılan farklı gün sayısı —
          uygulamayı açıp hiçbir şey yapmamak aktiflik sayılmıyor.
        </Prose>
        <div className="grid g4">
          <div className="tile">
            <div className="lab">KULLANICI</div>
            <div className="val">{o.kullanici}</div>
            <div className="dl">{o.aktif30} kişi son 30 günde kayıt bıraktı</div>
          </div>
          {/* Etiketler kısa: telefonda "ANTRENMAN · 30 GÜN" iki satıra sarıp
              kutuları farklı yükseklikte bırakıyordu. Açıklama alt satırda. */}
          <div className="tile">
            <div className="lab">ANTRENMAN</div>
            <div className="val">{o.antrenman30}</div>
            <div className="dl">son 30 günde tamamlanan</div>
          </div>
          <div className="tile">
            <div className="lab">TOPLAM</div>
            <div className="val">{o.toplamAntrenman}</div>
            <div className="dl">başından beri antrenman</div>
          </div>
        </div>
      </div>

      <div className="grid g2">
        {rows.map((u) => <KullaniciKarti key={u.userId} u={u} bugun={bugun} />)}
      </div>

      <div className="card">
        <h2>Son hareketler</h2>
        {gruplar.length === 0 ? (
          <p className="sub">Henüz kayıt yok.</p>
        ) : (
          <div className="feed">
            {gruplar.map((g) => (
              <div key={g.date} className="feedgun">
                <div className="feedtarih">
                  {trDate(g.date)} <span>· {gunceGore(g.date, bugun)}</span>
                </div>
                <ul>
                  {g.events.map((e, i) => (
                    <li key={i}>
                      <i data-kind={e.kind} />
                      <strong>{e.name}</strong> {e.text}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
        <Prose className="note">
          En yeni 40 hareket. Antrenman satırı yalnızca tamamlanan seanslar için düşüyor —
          açıp bırakılan bir antrenman burada görünmez.
        </Prose>
      </div>
    </>
  )
}

function KullaniciKarti({ u, bugun }: { u: UsageRow; bugun: string }) {
  const durum = aktiflik(u.lastEntry, bugun)
  const yuzde = ilerlemeYuzde(u.startWeightKg, u.currentWeightKg, u.targetWeightKg)
  const kalan = kalanKg(u.currentWeightKg, u.targetWeightKg)

  return (
    <div className="card usercard">
      <div className="userhead">
        <div>
          <h2>{u.name}</h2>
          <div className="usermail">{u.email}</div>
        </div>
        <span className="pill" data-durum={durum}>{AKTIFLIK_ETIKET[durum]}</span>
      </div>

      <div className="userstats">
        <div><b>{u.activeDays30}</b><span>aktif gün / 30</span></div>
        <div><b>{u.workouts30}</b><span>antrenman / 30</span></div>
        <div><b>{u.workoutsTotal}</b><span>toplam antrenman</span></div>
        <div><b>{u.weightsTotal}</b><span>tartı</span></div>
        <div><b>{u.activitiesTotal}</b><span>aktivite</span></div>
      </div>

      {yuzde === null ? (
        <p className="note" style={{ marginTop: 12 }}>
          {u.currentWeightKg === null
            ? 'Henüz hiç tartılmamış — ilerleme hesaplanamıyor.'
            : 'Başlangıç veya hedef kilo girilmemiş.'}
        </p>
      ) : (
        <div style={{ marginTop: 14 }}>
          <div className="userilerleme">
            <span>{GOAL_ETIKET[u.goal] ?? u.goal}</span>
            <span>
              {fmt(u.startWeightKg)} → <strong>{fmt(u.currentWeightKg ?? 0)}</strong> → {fmt(u.targetWeightKg)} kg
            </span>
          </div>
          <div className="bar-track"><div className="bar-fill" style={{ width: `${yuzde}%` }} /></div>
          <div className="note" style={{ marginTop: 6 }}>
            %{fmt(yuzde)} tamam
            {kalan !== null && (kalan > 0
              ? ` · hedefe ${fmt(Math.abs(kalan))} kg`
              : kalan < 0 ? ` · hedefi ${fmt(Math.abs(kalan))} kg geçti` : ' · hedefte')}
          </div>
        </div>
      )}

      <div className="note" style={{ marginTop: 12 }}>
        Son kayıt: {gunceGore(u.lastEntry, bugun)}
        {u.lastEntry && ` (${trDate(u.lastEntry)})`} · Son giriş:{' '}
        {gunceGore(u.lastLoginAt.slice(0, 10), bugun)} · Kayıt: {trDate(u.createdAt.slice(0, 10))}
      </div>
    </div>
  )
}
